import React, { useCallback, useState } from "react";
import { Avatar } from "@heroui/react";
import { Button, CustomModal, CustomCheckbox, Skeleton, showToast } from "../shared";
import SearchBar from "../shared/SearchBar";
import { useGroupMembers, useAvailableUsers, useAddGroupMembers, useRemoveGroupMember } from "../../lib/react-query/chat";
import { useGroupMemberUpdates } from "../../hooks/useGroupMemberUpdates";

const GroupMembersModal = ({
  open,
  onClose,
  channelId,
  channelName,
  isAdmin,
  currentUserId,
  onMembersUpdated,
}) => {
  const [showAddMembersSection, setShowAddMembersSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const {
    data: members = [],
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGroupMembers(channelId, { enabled: open });

  const {
    data: availableUsers = [],
    isLoading: availableUsersLoading,
    refetch: refetchAvailableUsers,
  } = useAvailableUsers(channelId, {
    enabled: open && showAddMembersSection,
  });

  const addMembersMutation = useAddGroupMembers();
  const removeMemberMutation = useRemoveGroupMember();

  const searchResults = searchQuery.trim()
    ? availableUsers.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableUsers;

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const handleCheckboxChange = useCallback((userId, checked) => {
    setSelectedUserIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  }, []);

  const handleAddMembers = async () => {
    if (selectedUserIds.length === 0) {
      showToast("Please select at least one member to add", "error", {
        position: "top-center",
      });
      return;
    }

    try {
      await addMembersMutation.mutateAsync({
        channelId,
        user_ids: selectedUserIds,
      });

      showToast("Members added successfully", "success", {
        position: "top-center",
      });

      setSelectedUserIds([]);
      setShowAddMembersSection(false);
      setSearchQuery("");
      onMembersUpdated?.();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to add members";
      showToast(errorMessage, "error", { position: "top-center" });
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (userId === currentUserId) {
      showToast("You cannot remove yourself from the group", "error", {
        position: "top-center",
      });
      return;
    }
    try {
      await removeMemberMutation.mutateAsync({
        channelId,
        user_id: userId,
      });

      showToast(`${userName} removed from the group`, "success", {
        position: "top-center",
      });

      onMembersUpdated?.();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to remove member";
      showToast(errorMessage, "error", { position: "top-center" });
    }
  };

  const handleToggleAddSection = () => {
    if (showAddMembersSection) {
      setShowAddMembersSection(false);
      setSearchQuery("");
      setSelectedUserIds([]);
    } else {
      setShowAddMembersSection(true);
      refetchAvailableUsers();
    }
  };

  const handleClose = () => {
    setShowAddMembersSection(false);
    setSearchQuery("");
    setSelectedUserIds([]);
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      refetchMembers();
    }
  }, [open, channelId, refetchMembers]);

  const handleMemberUpdate = useCallback(
    (type) => {
      switch (type) {
        case "member_added":
          showToast("New member added", "success", { position: "top-center" });
          refetchMembers();
          onMembersUpdated?.();
          break;
        case "member_removed":
          showToast("Member removed", "success", { position: "top-center" });
          refetchMembers();
          onMembersUpdated?.();
          break;
        case "list_updated":
          onMembersUpdated?.();
          break;
        default:
          break;
      }
    },
    [refetchMembers, onMembersUpdated]
  );

  useGroupMemberUpdates(channelId, handleMemberUpdate);

  const isRemoving = (member) =>
    removeMemberMutation.isPending &&
    removeMemberMutation.variables?.user_id === (member.id || member.user_id);

  return (
    <CustomModal
      title={`${channelName || "Group"} · ${members.length} member${members.length !== 1 ? "s" : ""}`}
      open={open}
      close={handleClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
      isDismissable={true}
    >
      <div className="flex flex-col gap-4">
        {/* ── Member List ── */}
        {membersLoading && members.length === 0 ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 flex flex-col gap-1.5">
                  <Skeleton className="w-32 h-4 rounded" />
                  <Skeleton className="w-48 h-3 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <p className="text-center text-mediumgrey py-8 font-sans">
            No members in this group
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto -mx-1 px-1">
            {members.map((member) => {
              const memberId = member.id || member.user_id;
              const memberName = member.name || member.user_name;
              const isSelf = memberId === currentUserId;

              return (
                <div
                  key={memberId}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface transition-colors"
                >
                  <Avatar
                    src={member.profile_image}
                    classNames={{
                      base: "bg-mediumgrey/30 min-w-10",
                      icon: "text-black/60",
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium font-sans text-sm truncate">
                        {memberName}
                        {isSelf ? " (You)" : ""}
                      </p>
                      {member.is_admin ? (
                        <span className="bg-secondary/10 text-secondary-dark text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                          Admin
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-mediumgrey truncate">
                      {member.email}
                    </p>
                  </div>
                  {isAdmin && !isSelf ? (
                    <Button
                      variant="outlined"
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveMember(memberId, memberName)}
                      disabled={isRemoving(member)}
                      loading={isRemoving(member)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Add Members Section ── */}
        {showAddMembersSection ? (
          <div className="border-t pt-4 flex flex-col gap-3">
            <SearchBar
              className="bg-surface-hover rounded"
              variant="bordered"
              value={searchQuery}
              onValueChange={handleSearch}
              placeholder="Search users to add..."
              onClear={() => setSearchQuery("")}
            />

            <div className="max-h-48 overflow-y-auto -mx-1 px-1">
              {availableUsersLoading ? (
                <div className="flex flex-col gap-3">
                  {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="flex items-center gap-3 p-3">
                      <Skeleton className="w-5 h-5 rounded" />
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="flex-1 flex flex-col gap-1.5">
                        <Skeleton className="w-28 h-4 rounded" />
                        <Skeleton className="w-40 h-3 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-mediumgrey py-6 font-sans text-sm">
                  {searchQuery
                    ? "No users found"
                    : "No users available to add"}
                </p>
              ) : (
                searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface cursor-pointer transition-colors"
                    onClick={() =>
                      handleCheckboxChange(
                        user.id,
                        !selectedUserIds.includes(user.id)
                      )
                    }
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedUserIds.includes(user.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCheckboxChange(
                          user.id,
                          !selectedUserIds.includes(user.id)
                        );
                      }
                    }}
                  >
                    <CustomCheckbox
                      checked={selectedUserIds.includes(user.id)}
                      onChange={(checked) =>
                        handleCheckboxChange(user.id, checked)
                      }
                    />
                    <Avatar
                      src={user.profile_image}
                      classNames={{
                        base: "bg-mediumgrey/30 min-w-10",
                        icon: "text-black/60",
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium font-sans text-sm truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-mediumgrey truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outlined"
                color="secondary"
                className="w-1/2"
                onClick={handleToggleAddSection}
                disabled={addMembersMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="w-1/2"
                onClick={handleAddMembers}
                disabled={
                  addMembersMutation.isPending || selectedUserIds.length === 0
                }
                loading={addMembersMutation.isPending}
              >
                {`Add ${selectedUserIds.length || ""} Member${selectedUserIds.length !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </div>
        ) : null}

        {/* ── Footer Actions ── */}
        {!showAddMembersSection ? (
          <div className="flex gap-2 border-t pt-4">
            <Button
              variant="outlined"
              color="secondary"
              className="w-1/2"
              onClick={handleClose}
            >
              Close
            </Button>
            {isAdmin ? (
              <Button className="w-1/2" onClick={handleToggleAddSection}>
                Add Members
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </CustomModal>
  );
};

export default GroupMembersModal;
